import { typographyTokens } from '../../../../theme/typographyTokens';
/**
 * DataFieldMapping Component
 * 
 * Admin page for adding data field locations to existing SEPs.
 * Allows admins to select multiple SEPs via checkboxes and add missing data fields
 * for specific departments in batch.
 * 
 * @module DataFieldMapping
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  useTheme,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  TextField,
} from '@mui/material';
import DataFieldMappingTable from './DataFieldMappingTable';
import { useGetAllSEPIDsQuery } from '../../../../../frontend/services/sepSlice';
import { SepOption } from './types';

const DataFieldMapping: React.FC = () => {
  const theme = useTheme();
  const [selectedSepIds, setSelectedSepIds] = useState<Set<number>>(new Set());
  const [searched, setSearched] = useState(false);
  const [sepIDs, setSepIDs] = useState<number[]>([]);
  const [filterText, setFilterText] = useState('');

  const { data: sepIDsData, error, isLoading } = useGetAllSEPIDsQuery();

  // Map API response to SEP options
  const sepOptions: SepOption[] = useMemo(() => {
    return Array.isArray(sepIDsData)
      ? sepIDsData.map((sep: any) => ({ id: sep.id, name: sep.name }))
      : [];
  }, [sepIDsData]);

  // Filter SEPs based on search text
  const filteredSeps = useMemo(() => {
    if (!filterText.trim()) return sepOptions;
    const searchLower = filterText.toLowerCase();
    return sepOptions.filter(
      (sep) =>
        sep.id.toString().includes(searchLower) ||
        sep.name.toLowerCase().includes(searchLower)
    );
  }, [sepOptions, filterText]);

  const handleCheckboxChange = useCallback((sepId: number, checked: boolean) => {
    setSelectedSepIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(sepId);
      } else {
        newSet.delete(sepId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedSepIds(new Set(filteredSeps.map((sep) => sep.id)));
    } else {
      setSelectedSepIds(new Set());
    }
  }, [filteredSeps]);

  const handleGetDetails = useCallback(() => {
    if (selectedSepIds.size > 0) {
      setSepIDs(Array.from(selectedSepIds));
      setSearched(true);
    }
  }, [selectedSepIds]);

  const handleClear = useCallback(() => {
    setSelectedSepIds(new Set());
    setSearched(false);
    setSepIDs([]);
    setFilterText('');
  }, []);

  const allSelected = filteredSeps.length > 0 && filteredSeps.every((sep) => selectedSepIds.has(sep.id));
  const someSelected = filteredSeps.some((sep) => selectedSepIds.has(sep.id)) && !allSelected;

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 2 }}>
        <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.xl, mb: 2 }}>
          Add data field to all SEPS
        </Typography>
        <Typography sx={{ fontSize: typographyTokens.fontSizes.body, color: theme.palette.text.secondary, mb: 2 }}>
          Selected field will be added to the SEP Process Supervisor details tab on all SEPs
        </Typography>
        {isLoading ? (
          <Typography sx={{ fontSize: typographyTokens.fontSizes.base, color: theme.palette.primary.main, mt: 2 }}>
            Loading SEP list...
          </Typography>
        ) : error ? (
          <Typography sx={{ fontSize: typographyTokens.fontSizes.base, color: theme.palette.error.main, mt: 2 }}>
            Failed to load SEP list. Please try again later.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Filter input */}
            <TextField
              label="Filter SEPs"
              variant="outlined"
              size="small"
              value={filterText}
              onChange={(e: any) => setFilterText(e.target.value)}
              placeholder="Type to filter by ID or name..."
              sx={{ maxWidth: 400 }}
            />

            {/* Select All checkbox */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e: any) => handleSelectAll(e.target.checked)}
                  />
                }
                label={<Typography sx={{ fontWeight: typographyTokens.fontWeights.semibold }}>Select All ({filteredSeps.length} SEPs)</Typography>}
              />
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: typographyTokens.fontSizes.body }}>
                {selectedSepIds.size} selected
              </Typography>
            </Box>

            <Divider />

            {/* SEP checkboxes in scrollable container */}
            <Box
              sx={{
                maxHeight: 300,
                overflowY: 'auto',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 2,
              }}
            >
              <FormGroup>
                {filteredSeps.map((sep) => (
                  <FormControlLabel
                    key={sep.id}
                    control={
                      <Checkbox
                        checked={selectedSepIds.has(sep.id)}
                        onChange={(e: any) => handleCheckboxChange(sep.id, e.target.checked)}
                        size="small"
                      />
                    }
                    label={`${sep.id} - ${sep.name}`}
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: typographyTokens.fontSizes.body } }}
                  />
                ))}
              </FormGroup>
              {filteredSeps.length === 0 && (
                <Typography sx={{ color: theme.palette.text.secondary, fontSize: typographyTokens.fontSizes.body }}>
                  No SEPs match the filter.
                </Typography>
              )}
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, justifyContent: 'flex-end', width: '100%' }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  textTransform: 'none',
                }}
                onClick={handleGetDetails}
                disabled={selectedSepIds.size === 0}
              >
                GET DETAILS ({selectedSepIds.size})
              </Button>
              <Button
                variant="outlined"
                sx={{ textTransform: 'none' }}
                onClick={handleClear}
              >
                CLEAR
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
      {searched && sepIDs.length > 0 && <DataFieldMappingTable sepIDs={sepIDs} />}
    </Box>
  );
};

export default DataFieldMapping;
